import { createFileRoute, Outlet, useNavigate, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { BottomTabs } from "@/components/BottomTabs";

import { BiometricGate } from "@/components/BiometricGate";
import { isCrawlMode } from "@/lib/crawl-mode";
import { OfflineModeChip } from "@/components/OfflineModeChip";
import { hydrateCachedTrip } from "@/lib/offline/trip-cache";
import { logOfflineAuth, isOfflineNow } from "@/lib/offline/offline-auth";
import { isUnlockedThisLaunch } from "@/lib/native";



export const Route = createFileRoute("/_authenticated")({
  component: AuthLayout,
});

// Routes that should NOT show the bottom tab bar (full-screen flows or screens with their own nav).
const HIDE_TABS_ON = ["/join", "/admin", "/face-id-intro"];

// If session resolution takes longer than this, assume the network is gone and
// route the user to the offline-control screen instead of bouncing them to a
// useless /login page.
const SESSION_WATCHDOG_MS = 2000;

function AuthLayout() {
  const { t } = useTranslation();
  const { session, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const crawl = isCrawlMode();
  const [watchdogFired, setWatchdogFired] = useState(false);
  const loggedSessionRef = useState({ logged: false })[0];

  // Watchdog: if loading hasn't resolved within SESSION_WATCHDOG_MS AND we're
  // offline, give up waiting and head to /offline-control. Auth network reads
  // can hang indefinitely on a plane / behind a captive portal.
  useEffect(() => {
    if (crawl || !loading) return;
    const t = window.setTimeout(() => {
      if (isOfflineNow()) setWatchdogFired(true);
    }, SESSION_WATCHDOG_MS);
    return () => window.clearTimeout(t);
  }, [loading, crawl]);

  // Log the "local session found" decision exactly once per auth gate when
  // the user has a cached session and is currently offline.
  useEffect(() => {
    if (crawl) return;
    if (!session) return;
    if (loggedSessionRef.logged) return;
    if (isOfflineNow()) {
      loggedSessionRef.logged = true;
      logOfflineAuth("local session found", "offline-controls");
    }
  }, [session, crawl, loggedSessionRef]);

  // Soft re-auth prompt: when a background refresh fails with invalid_grant,
  // we keep the user in the app for this session and surface a toast asking
  // them to sign in again next launch. Fires at most once per launch.
  useEffect(() => {
    let shown = false;
    const onNeedsReauth = () => {
      if (shown) return;
      shown = true;
      toast(t("auth.reauth_title"), {
        description: t("auth.reauth_desc"),
        duration: 6000,
      });
    };
    window.addEventListener("roava:needs-reauth", onNeedsReauth);
    return () => window.removeEventListener("roava:needs-reauth", onNeedsReauth);
  }, [t]);


  useEffect(() => {
    if (crawl) return;
    if (loading && !watchdogFired) return;
    if (session) return;

    const offline = isOfflineNow() || watchdogFired;
    if (!offline) {
      // Belt-and-braces: if BiometricGate already unlocked this launch and
      // the session vanished (e.g. background refresh nuked it despite the
      // suppression guard), DON'T bounce to /login mid-trip — the user is
      // mid-task. The needs-reauth toast will prompt re-login next launch.
      if (isUnlockedThisLaunch()) {
        // eslint-disable-next-line no-console
        console.warn("[auth-gate] session null after unlock — staying on route, awaiting next launch");
        return;
      }
      nav({ to: "/login" });
      return;
    }


    // Offline + no session: prefer Offline Controls if we have a cached trip,
    // otherwise send the user to /login which renders the offline fallback
    // panel (NOT a broken sign-in form).
    let cancelled = false;
    void hydrateCachedTrip().then((trip) => {
      if (cancelled) return;
      if (trip) {
        logOfflineAuth("cached trip found", "offline-controls");
        nav({ to: "/offline-control" });
      } else {
        logOfflineAuth("no cached session", "offline-login-required");
        nav({ to: "/login" });
      }
    });
    return () => { cancelled = true; };
  }, [loading, session, nav, crawl, watchdogFired]);

  if (loading && !crawl && !watchdogFired) {
    // Quiet for the first 250 ms (most cold launches resolve in this window —
    // no spinner flash). After that, show a minimal spinner so slow/offline
    // launches don't read as frozen. No logo, no text — the BiometricGate
    // takes over the moment auth resolves.
    return <AuthLoadingScreen />;
  }
  if (!session && !crawl) return null;

  const showTabs = !HIDE_TABS_ON.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));

  return (
    <BiometricGate>
      <Outlet />
      <OfflineModeChip />
      {showTabs && <BottomTabs />}
    </BiometricGate>
  );
}

function AuthLoadingScreen() {
  const { t } = useTranslation();
  const [showSpinner, setShowSpinner] = useState(false);
  useEffect(() => {
    const tm = window.setTimeout(() => setShowSpinner(true), 250);
    return () => window.clearTimeout(tm);
  }, []);
  return (
    <div className="min-h-screen bg-background grid place-items-center" aria-label={t("auth.loading_aria")}>
      {showSpinner && (
        <div
          className="size-6 rounded-full border-2 border-muted border-t-primary animate-spin opacity-70"
          role="status"
          aria-label={t("auth.checking_session_aria")}
        />
      )}
    </div>
  );
}
